import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LANGUAGES } from "../data/languages";
import { getState } from "../data/locations";
import {
  generateFictionalPerson,
  mergeWithFiller,
  generateSpecimenAadhaar,
  generateSpecimenVid,
  DEFAULT_PHOTO_FRAME,
} from "../utils/fictional";
import { buildSpecimenQr } from "../utils/qr";
import { cropToPassport, fetchPoolPhoto, fetchRandomPhoto } from "../utils/photo";
import { validate } from "../utils/validate";
import { DEFAULT_POOL, loadPool, savePool, randomNameFrom } from "../utils/namePool";
import { transliterateText } from "../utils/transliterate";
import api from "../api/client";

const CardContext = createContext(null);

const STORAGE_KEY = "foliox-card-draft";

const AUTO_FIELDS = { name: "nameLocal", address: "addressLocal" };

export const EMPTY_FORM = {
  country: "IN",
  state: "GJ",
  name: "",
  nameLocal: "",
  dob: "",
  gender: "",
  address: "",
  addressLocal: "",
  issuedDate: "",
  photo: null,
};

/**
 * The card draft is restored across reloads, but the portrait is dropped: a
 * cropped photo is a data URL of a few hundred kilobytes and localStorage
 * caps out around five megabytes, so persisting it is the one thing likely to
 * blow the quota and lose the rest of the draft with it.
 */
function loadDraft() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_FORM;
    const parsed = JSON.parse(raw);
    return { ...EMPTY_FORM, ...parsed, photo: null };
  } catch (err) {
    return EMPTY_FORM;
  }
}

function saveDraft(form) {
  try {
    const { photo, ...rest } = form;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  } catch (err) {
    /* private mode or a full quota; the draft simply is not persisted */
  }
}

/**
 * Holds the card being built.
 *
 * The form lives here rather than in a page because the editor and the
 * preview are now separate routes - navigating between them must not reset
 * what the user has typed or re-roll the generated identity.
 */
export function CardProvider({ children }) {
  const [form, setForm] = useState(loadDraft);
  const [seed, setSeed] = useState(0);
  const [namePool, setNamePoolState] = useState(loadPool);
  const [photoError, setPhotoError] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [qr, setQr] = useState("");

  useEffect(() => {
    saveDraft(form);
  }, [form]);

  /* eslint-disable react-hooks/exhaustive-deps */
  // `seed` is a dependency without being read: bumping it is how Regenerate
  // asks for a fresh draw.
  const filler = useMemo(
    () => generateFictionalPerson(form.state),
    [form.state, seed]
  );
  const ids = useMemo(
    () => ({ aadhaar: generateSpecimenAadhaar(), vid: generateSpecimenVid() }),
    [seed]
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  const errors = useMemo(() => validate(form), [form]);
  const data = useMemo(
    () => ({ ...mergeWithFiller(form, filler), ...ids }),
    [form, filler, ids]
  );

  const state = getState(form.state);
  const lang = LANGUAGES[state.lang];

  /**
   * The QR now encodes the whole card rather than just the number, so it has
   * to be rebuilt whenever any printed field changes - including the photo,
   * which costs a canvas resize and a JPEG encode. Typing a name would
   * otherwise re-run that on every keystroke, so the rebuild trails the last
   * edit by a beat.
   */
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      buildSpecimenQr(data)
        .then((url) => {
          if (!cancelled) setQr(url);
        })
        .catch(() => {
          if (!cancelled) setQr("");
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [data]);

  // Typing the English half of a name or address keeps the local-script half
  // in sync live, every keystroke - this overwrites whatever was in the
  // local field before, including something typed there directly, which is
  // the point: it's meant to always reflect what the English field says
  // right now, the same way a translated field would. Editing the local
  // field itself is untouched - only typing in the English field drives it.
  // Only that direction: going local-script -> English is a different, much
  // harder problem (reverse transliteration is ambiguous in ways forward
  // transliteration is not) and isn't attempted.
  const setField = useCallback(
    (field, value) => {
      setForm((prev) => {
        const next = { ...prev, [field]: value };
        const localField = AUTO_FIELDS[field];
        if (localField) {
          next[localField] = value.trim() ? transliterateText(value, lang.script) || "" : "";
        }
        return next;
      });
    },
    [lang.script]
  );

  const regenerate = useCallback(() => setSeed((s) => s + 1), []);

  const reset = useCallback(() => {
    setForm(EMPTY_FORM);
    setPhotoError("");
    setSeed((s) => s + 1);
  }, []);

  /**
   * Every fresh photo starts framed at whatever offset/zoom other people
   * have most often settled on (the server's crowd-sourced "smart
   * default"), not dead centre - falls back to identity framing (no crop
   * adjustment beyond the passport-ratio crop already applied) if the
   * server has too few samples yet or is unreachable.
   */
  const applySmartFrame = useCallback(async () => {
    try {
      const res = await api.get("/api/photos/frame/smart-default");
      setForm((prev) => ({
        ...prev,
        photoFrame: { x: res.offsetX, y: res.offsetY, scale: res.scale },
      }));
    } catch (err) {
      setForm((prev) => ({ ...prev, photoFrame: DEFAULT_PHOTO_FRAME }));
    }
  }, []);

  const setPhoto = useCallback(
    async (file) => {
      setPhotoError("");
      try {
        const cropped = await cropToPassport(file);
        setForm((prev) => ({ ...prev, photo: cropped }));
        applySmartFrame();
      } catch (err) {
        setPhotoError(err.message);
        setForm((prev) => ({ ...prev, photo: null }));
      }
    },
    [applySmartFrame]
  );

  const setPhotoDataUrl = useCallback(
    (dataUrl) => {
      setPhotoError("");
      setForm((prev) => ({ ...prev, photo: dataUrl }));
      applySmartFrame();
    },
    [applySmartFrame]
  );

  /**
   * Prefers the shared pool - portraits other users uploaded and left public
   * - and only falls back to Unsplash when the pool is empty or unreachable.
   * That way a fresh install still works without an API key once a few photos
   * have been shared, and the stock service is spared the request otherwise.
   */
  const randomPhoto = useCallback(async () => {
    setPhotoError("");
    setPhotoBusy(true);
    try {
      const pooled = await fetchPoolPhoto();
      if (pooled) {
        setForm((prev) => ({ ...prev, photo: pooled }));
        applySmartFrame();
        return;
      }
      const photo = await fetchRandomPhoto(form.gender);
      setForm((prev) => ({ ...prev, photo }));
      applySmartFrame();
    } catch (err) {
      setPhotoError(err.message || "Could not fetch a random photo.");
    } finally {
      setPhotoBusy(false);
    }
  }, [form.gender, applySmartFrame]);

  const clearPhoto = useCallback(() => {
    setPhotoError("");
    setForm((prev) => ({ ...prev, photo: null, photoFrame: DEFAULT_PHOTO_FRAME }));
  }, []);

  /** Live drag/zoom updates - local only, no network call per frame. */
  const setPhotoFrame = useCallback((frame) => {
    setForm((prev) => ({ ...prev, photoFrame: frame }));
  }, []);

  /** Fed back once the user settles on a framing (drag/zoom release), so
      the crowd-sourced smart default keeps learning. Fire-and-forget, like
      the name/address pool contribution below - never blocks or surfaces
      an error over an enrichment signal. */
  const recordPhotoFrame = useCallback((frame) => {
    api
      .post("/api/photos/frame", { offsetX: frame.x, offsetY: frame.y, scale: frame.scale })
      .catch(() => {});
  }, []);

  const setNamePool = useCallback((text) => {
    setNamePoolState(text);
    savePool(text);
  }, []);

  const resetPool = useCallback(() => {
    setNamePoolState(DEFAULT_POOL);
    savePool(DEFAULT_POOL);
  }, []);

  /**
   * Tries the shared, server-side community pool first - names other users
   * have actually typed and had transliterated, for this same script - and
   * only falls back to the local per-browser pool if the server has nothing
   * yet for this script or is unreachable. The feature must keep working
   * offline, so a failed request here is silent, not an error.
   */
  const shuffleName = useCallback(async () => {
    try {
      const res = await api.get(`/api/community/names/random?script=${lang.script}`);
      if (res.firstLocal) {
        const firstEn = res.firstEn || res.firstLocal;
        const name = res.lastEn ? `${firstEn} ${res.lastEn}` : firstEn;
        const nameLocal = res.lastLocal
          ? `${res.firstLocal} ${res.lastLocal}`
          : res.firstLocal;
        setForm((prev) => ({ ...prev, name, nameLocal }));
        return true;
      }
    } catch (err) {
      /* community pool unavailable - fall through to the local one */
    }

    const picked = randomNameFrom(namePool);
    if (!picked) return false;

    // The pool is free-typed English with no local-script counterpart, so the
    // whole name is transliterated - both halves. This used to transliterate
    // the given name only and staple the literal word for "sample" on as the
    // surname, so a shuffle produced "Rohit Verma" in English beside "Rohit
    // Namuna" in the local script.
    const localName = picked
      .split(/\s+/)
      .map((part) => transliterateText(part, lang.script))
      .filter(Boolean)
      .join(" ");

    setForm((prev) => ({
      ...prev,
      name: picked,
      nameLocal: localName || prev.nameLocal,
    }));
    return true;
  }, [namePool, lang]);

  /**
   * Feeds a name or address the user actually typed themselves back into
   * the shared pool, so future random draws benefit from it too. Called
   * once a card generates successfully - not on every keystroke, so the
   * pool fills with things people meant to keep, not half-typed drafts.
   * Fire-and-forget: this is a nice-to-have enrichment, never something a
   * generation should be blocked or failed by.
   */
  const contributeToPool = useCallback(() => {
    if (form.name.trim() && data.nameLocal) {
      const [firstEn, ...restEn] = form.name.trim().split(/\s+/);
      const [firstLocal, ...restLocal] = data.nameLocal.trim().split(/\s+/);
      api
        .post("/api/community/names", {
          script: lang.script,
          firstEn,
          firstLocal,
          lastEn: restEn.join(" ") || undefined,
          lastLocal: restLocal.join(" ") || undefined,
        })
        .catch(() => {});
    }
    if (form.address.trim() && data.addressLocal) {
      api
        .post("/api/community/addresses", {
          script: lang.script,
          addressEn: form.address.trim(),
          addressLocal: data.addressLocal.trim(),
        })
        .catch(() => {});
    }
  }, [form.name, form.address, data.nameLocal, data.addressLocal, lang.script]);

  const value = useMemo(
    () => ({
      form,
      data,
      errors,
      lang,
      state,
      qr,
      seed,
      namePool,
      photoError,
      photoBusy,
      hasErrors: Object.keys(errors).length > 0,
      setField,
      regenerate,
      reset,
      setPhoto,
      setPhotoDataUrl,
      randomPhoto,
      clearPhoto,
      setPhotoFrame,
      recordPhotoFrame,
      setNamePool,
      resetPool,
      shuffleName,
      contributeToPool,
    }),
    [
      form,
      data,
      errors,
      lang,
      state,
      qr,
      seed,
      namePool,
      photoError,
      photoBusy,
      setField,
      regenerate,
      reset,
      setPhoto,
      setPhotoDataUrl,
      randomPhoto,
      contributeToPool,
      clearPhoto,
      setPhotoFrame,
      recordPhotoFrame,
      setNamePool,
      resetPool,
      shuffleName,
    ]
  );

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
}

export function useCard() {
  const ctx = useContext(CardContext);
  if (!ctx) throw new Error("useCard must be used inside <CardProvider>");
  return ctx;
}

export default CardContext;
